import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `Desde 2006 a 2024, se observa una baja en la proporción de niñas y niños en hogares sin acceso a servicios básicos. En el primer año de la serie, la proporción 
          correspondía a un 10% y en 2024, el último año de medición, a un 5%. Sin embargo, esta baja no ha sido constante, observándose un alza en los años 2011 y 2020. 
          El punto más bajo fue en 2015, con un 4% de niñas y niños viviendo en hogares sin acceso a servicios básicos.`,
    link: "https://public.flourish.studio/visualisation/20952227/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `No hay diferencias estadísticamente significativas en la proporción de niñas y niños que vive en hogares sin acceso a servicios básico según sexo. Además, las 
          variaciones a lo largo de la serie de tiempo son iguales.`,
    link: "https://public.flourish.studio/visualisation/20952311/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `No se observan diferencias estadísticamente significativas de acuerdo con los tramos etarios. Los efectos que tiene la falta de servicios básicos difiere según 
          la edad de la niña o el niño, por lo que las intervenciones deben ser focalizadas, atendiendo las necesidades y desafíos de cada etapa del desarrollo.`,
    link: "https://public.flourish.studio/visualisation/20952322/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `La proporción de niñas y niños sin acceso a servicios básicos fue similar entre aquellas y aquellos que nacieron en Chile y los que nacieron en otro país en la 
          mayoría de los períodos analizados hasta 2022, con la excepción de 2015, donde la proporción fue levemente mayor para las niñas y niños nacidos en el país (4% 
          versus 1%). Sin embargo, para el año 2024 esta tendencia cambia significativamente. En la última medición se observa que las niñas y niños nacidos fuera del país 
          presentan una falta de acceso a servicios básicos en mayor medida que los nacidos en Chile (7% versus 4%).`,
    link: "https://public.flourish.studio/visualisation/20952339/embed",
  },
  {
    id: "graph6",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `En toda la serie de tiempo, ha existido una brecha en el acceso a servicios básicos entre niñas y niños que se identifican con pueblos originarios y aquellos que 
          no lo hacen. En el año 2006, el 27% de las niñas y niños pertenecientes a estos pueblos no tenían acceso a servicios básicos en sus hogares, versus un 8% de niñas 
          y niños que no pertenecen. En 2024, las proporciones corresponden a 8% y 4%, respectivamente. Aunque la brecha se ha acortado, continúa habiendo una diferencia en 
          el acceso a servicios básicos.`,
    link: "https://public.flourish.studio/visualisation/20952375/embed",
  },
  {
    id: "graph7",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `Existe una estrecha relación entre el nivel de ingresos familiares y el acceso a servicios básicos: a mayor nivel de ingresos (quintil más alto), menor es la 
          proporción de niñas y niños con carencias. En la última medición, un 8% de niñas y niños del primer quintil (I), y un 1% del último quintil (V) no tienen acceso 
          a servicios básicos. La serie muestra una tendencia estable tras una reducción inicial de la desigualdad: la brecha de 10 puntos observada en 2006 entre el primer 
          y segundo quintil se acortó a la mitad a partir de 2011, situándose desde entonces cerca de los 5 puntos porcentuales.`,
    link: "https://public.flourish.studio/visualisation/20952378/embed",
  },
  {
    id: "graph5",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `El análisis del acceso a servicios básicos de acuerdo a la zona de residencia revela brechas considerables. Los datos muestran que en todos los años de medición 
          las zonas rurales presentan un acceso significativamente menor a estos servicios en comparación con las áreas urbanas. En 2024, la proporción de niñas y niños en 
          hogares sin acceso a servicios básicos en zonas rurales alcanza el 17%, mientras que en zonas urbanas el 3%. Para abordar estas brechas, podrían ser necesarias 
          estrategias y políticas específicas que incluyan inversiones en infraestructura, programas de desarrollo comunitario y mejor acceso a servicios públicos en las zonas 
          rurales.`,
    link: "https://public.flourish.studio/visualisation/20952314/embed",
  },
  {
    id: "graph8",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `Las regiones de la zona Norte y zona Sur del país tienden a presentar una mayor proporción de niñas y niños en hogares sin acceso a servicios básicos en 2024. Por 
          un lado, destacan las regiones de Tarapacá (15%), Arica y Parinacota (12%), y Atacama (11%) y, en el otro extremo del país, se observa a Los Ríos, Los Lagos (ambos 
          10%) y La Araucanía (8%). Las regiones de la zona Centro y del extremo Sur tienden a presentar una menor proporción de niñas y niños sin acceso a servicios básicos, 
          encontrándose todas bajo el 6%.`,
    link: "https://public.flourish.studio/visualisation/22767061/embed",
  },
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;